import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { CreditCard, Link2, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

// v3.15-963: Admin widget that lists Unlinked credit card submissions and
// lets a director attach each one to an existing client. Visible only to
// admin/director roles. Queries `/api/credit-card-auth/unlinked` and
// `/api/credit-card-auth/{id}/link`.

const ADMIN_DIRECTOR_ROLES = new Set([
  'ptc_admin',
  'admin',
  'ptc_director',
  'company_admin',
  'company_director',
]);

const formatCurrency = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

export const UnlinkedCardSubmissionsCard = ({ userRole }) => {
  const canSee = ADMIN_DIRECTOR_ROLES.has((userRole || '').toLowerCase());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);

  const fetchRows = useCallback(async () => {
    if (!canSee) return;
    setLoading(true);
    try {
      const { data } = await api.get('/credit-card-auth/unlinked');
      setRows(data?.records || []);
    } catch {
      // Silent.
    } finally {
      setLoading(false);
    }
  }, [canSee]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Refresh every 60s — EQF posts can land at any time.
  useEffect(() => {
    if (!canSee) return undefined;
    const t = setInterval(fetchRows, 60_000);
    return () => clearInterval(t);
  }, [canSee, fetchRows]);

  const openLink = (row) => {
    setActiveRow(row);
    setSearch(row.email || row.phone || row.name_on_card || '');
    setResults([]);
    setLinkOpen(true);
  };

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get(`/clients/search?q=${encodeURIComponent(q)}`);
      // Different endpoints return different shapes; normalize.
      const list = Array.isArray(data)
        ? data
        : data?.clients || data?.results || [];
      setResults(list.slice(0, 12));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search.
  useEffect(() => {
    if (!linkOpen) return undefined;
    const t = setTimeout(() => runSearch(search), 300);
    return () => clearTimeout(t);
  }, [search, linkOpen, runSearch]);

  const linkToClient = async (clientId) => {
    if (!activeRow?.id || !clientId) return;
    setLinking(true);
    try {
      await api.post(`/credit-card-auth/${activeRow.id}/link`, { client_id: clientId });
      toast.success('Linked to client.');
      setLinkOpen(false);
      setActiveRow(null);
      await fetchRows();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to link.');
    } finally {
      setLinking(false);
    }
  };

  // v3.15-1048: Create authoritative client (full_name + email + phone) and
  // immediately link this card submission. Visible ONLY when search returns
  // 0 results. Bypasses Bill.com / Finturf / payroll workflow triggers.
  const createClientAndLink = async () => {
    if (!activeRow?.id) return;
    const fullName = (activeRow.name_on_card || '').trim();
    if (!fullName) {
      toast.error('Submission has no name to create a client from.');
      return;
    }
    setLinking(true);
    try {
      const { data } = await api.post(
        `/credit-card-auth/${activeRow.id}/create-client-and-link`,
        {
          full_name: fullName,
          email:     (activeRow.email || '').trim() || null,
          phone:     (activeRow.phone || '').trim() || null,
        }
      );
      toast.success(`Client created (${data.created_client_id?.slice(0, 8)}…) and submission linked.`);
      setLinkOpen(false);
      setActiveRow(null);
      await fetchRows();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // 409 → duplicate. detail is a dict with duplicate_client_id.
      if (err?.response?.status === 409 && detail && typeof detail === 'object' && detail.duplicate_client_id) {
        toast.info(`Existing client found: ${detail.duplicate_full_name || detail.duplicate_email}. Linking instead.`);
        await linkToClient(detail.duplicate_client_id);
        return;
      }
      const msg = (typeof detail === 'string') ? detail : (detail?.message || 'Failed to create client.');
      toast.error(msg);
    } finally {
      setLinking(false);
    }
  };

  const dismiss = async (row) => {
    if (!window.confirm(`Permanently delete the unlinked card submission (${row.confirmation_number || row.last4})?`)) return;
    try {
      // v3.18-1278 GHOST-OPS-001 — wire the real DELETE endpoint.
      await api.delete(`/credit-card-auth/unlinked/${row.id}`);
      toast.success('Unlinked card submission deleted.');
      setRows((prev) => prev.filter((x) => x.id !== row.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete submission.');
    }
  };

  if (!canSee) return null;
  if (!loading && rows.length === 0) return null;

  return (
    <>
      <Card className="border-amber-300 bg-amber-50/40" data-testid="unlinked-cc-submissions-card">
        <CardHeader className="py-2 px-3">
          <CardTitle className="text-sm font-semibold flex items-center justify-between text-amber-900">
            <span className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Unlinked Card Submissions
              <Badge className="bg-amber-200 text-amber-900 text-[10px]" data-testid="unlinked-cc-count">
                {rows.length}
              </Badge>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchRows}
              disabled={loading}
              data-testid="unlinked-cc-refresh"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="rounded-md border border-amber-200 bg-white divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {rows.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-2 px-3 py-2 text-xs"
                data-testid={`unlinked-cc-row-${r.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-slate-700">{r.confirmation_number}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-slate-700 truncate">{r.name_on_card || '—'}</span>
                    {r.brand && <Badge variant="outline" className="text-[10px]">{r.brand}</Badge>}
                    {r.last4 && (
                      <span className="text-slate-400 font-mono">···· {r.last4}</span>
                    )}
                  </div>
                  <div className="text-slate-500 mt-0.5">
                    {r.email || '—'} · {r.phone || '—'} · {formatCurrency(r.amount)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    onClick={() => openLink(r)}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid={`unlinked-cc-link-btn-${r.id}`}
                  >
                    <Link2 className="h-3 w-3 mr-1" />
                    Link to Client
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismiss(r)}
                    className="text-slate-500 hover:text-rose-700"
                    data-testid={`unlinked-cc-dismiss-${r.id}`}
                    title="Delete this unlinked submission"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-lg" data-testid="link-cc-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-amber-600" />
              Link Card Submission to Client
            </DialogTitle>
          </DialogHeader>
          {activeRow && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs space-y-0.5">
              <div className="font-mono">{activeRow.confirmation_number}</div>
              <div className="text-slate-700">
                {activeRow.name_on_card} · {activeRow.email || '—'} · {activeRow.phone || '—'}
              </div>
              <div className="text-slate-500">
                {activeRow.brand} ···· {activeRow.last4} · {formatCurrency(activeRow.amount)}
              </div>
            </div>
          )}
          <Input
            placeholder="Search clients by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="link-cc-search"
            autoFocus
          />
          <div className="rounded-md border border-slate-200 max-h-64 overflow-y-auto">
            {searching && (
              <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                Searching...
              </div>
            )}
            {!searching && results.length === 0 && search.length >= 2 && (
              <div className="px-3 py-2 text-xs">
                <div className="text-slate-500 mb-2">No clients found.</div>
                {activeRow?.name_on_card && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={createClientAndLink}
                    disabled={linking}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                    data-testid="link-cc-create-client-btn"
                  >
                    {linking ? (
                      <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Creating…</>
                    ) : (
                      <>Create Client + Link <span className="ml-1 text-emerald-100 font-mono">({activeRow.name_on_card})</span></>
                    )}
                  </Button>
                )}
              </div>
            )}
            {results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => linkToClient(c.id)}
                disabled={linking}
                className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-center justify-between text-xs disabled:opacity-50"
                data-testid={`link-cc-result-${c.id}`}
              >
                <div>
                  <div className="font-medium text-slate-800">{c.full_name}</div>
                  <div className="text-slate-500">
                    {c.email || '—'} · {c.phone || '—'}
                  </div>
                </div>
                {linking && <Loader2 className="h-3 w-3 animate-spin" />}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLinkOpen(false)}
              disabled={linking}
              data-testid="link-cc-cancel-btn"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UnlinkedCardSubmissionsCard;
